import { debounce } from 'lodash';
import { VulcanHook, VulcanThis } from '../app';
import { isMobile } from '../utils/ismobile';

type Globals = {
  elements: {
    closeButton: HTMLButtonElement | null;
    input: HTMLInputElement | null;
    clearButton: HTMLButtonElement | null;
    predictiveContainer: HTMLElement | null;
    searchIconDesktop: HTMLElement | null;
    headerSearchInputDesktop: HTMLInputElement | null;
    headerVisible: HTMLElement | null;
  };
  closeOnMouseOut: boolean;
};
type Callbacks = {
  setup: () => void;
  handleClear: () => void;
  handleClickOutside: (e: Event) => void;
  handleFocusOut: (e: FocusEvent) => void;
  handleEscape: (e: KeyboardEvent) => void;
  handleEnter: (e: KeyboardEvent) => void;
  handleInput: (e: KeyboardEvent) => void;
  handleKeyDown: (e: KeyboardEvent) => void;
  addEventListeners: () => void;
  openOverlay: () => void;
  closeOverlay: () => void;
  toggleOverlay: (e: Event) => void;
  setIconFocus: () => void;
  isOverlayClosed: () => boolean;
};
type Hook = VulcanHook & Callbacks;
type This = VulcanThis & Callbacks & Globals;

export const predictiveSearch: Hook = {
  mounted(this: This) {
    this.closeOnMouseOut = this.el.hasAttribute('vulcan-close-on-mouse-out');

    this.setup();
    this.addEventListeners();
  },
  setup(this: This) {
    const closeButton = this.el.querySelector("button[name='close']") as HTMLButtonElement | null;
    const input = this.el.querySelector("input[name='search']") as HTMLInputElement | null;
    const clearButton = this.el.querySelector("button[name='clear']") as HTMLButtonElement | null;
    const headerVisible = document.querySelector(
      '[vulcan-id=header-visible]'
    ) as HTMLElement | null;
    const predictiveContainer = this.el.querySelector(
      '[vulcan-id=predictiveContainer]'
    ) as HTMLButtonElement | null;
    const searchIconDesktop = document.querySelector(
      '[vulcan-id=search-icon-desktop]'
    ) as HTMLElement | null;
    const headerSearchInputDesktop = document.querySelector(
      '[vulcan-id=header-search-input-desktop]'
    ) as HTMLInputElement | null;

    this.elements = {
      closeButton,
      input,
      clearButton,
      predictiveContainer,
      searchIconDesktop,
      headerSearchInputDesktop,
      headerVisible
    };
  },
  // Send the `clear` event on button click.
  handleClear(this: This) {
    if (this.elements.input) {
      this.pushEventTo('#predictive_search', 'clear', {});
      this.elements.input.value = '';
      this.elements.input.focus();
    }
  },
  handleClickOutside(this: This, e: Event) {
    if (this.elements.headerSearchInputDesktop && !isMobile()) {
      if (!this.elements.headerSearchInputDesktop?.contains(e.target as Node)) {
        this.closeOverlay();
      }
      return;
    }

    if (!this.elements.predictiveContainer?.contains(e.target as Node)) {
      this.closeOverlay();
    }
  },
  // This keeps tabbing focus within the overlay
  handleFocusOut(this: This, e: FocusEvent) {
    if (!this.el.contains(e.relatedTarget as Node | null)) {
      if (this.elements.headerSearchInputDesktop) {
        this.elements.headerSearchInputDesktop.focus();
        return;
      }

      this.elements.input?.focus();
    }
  },
  handleEscape(this: This, e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.closeOverlay();
      this.setIconFocus();
    }
  },
  handleEnter(this: This, e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.setIconFocus();
    }
  },
  handleInput(this: This, e: KeyboardEvent) {
    const target = e.target as HTMLInputElement;
    const userTyped = target.value.length > 0;

    userTyped ? this.isOverlayClosed() && this.openOverlay() : this.closeOverlay();

    if (this.elements.headerSearchInputDesktop) {
      debounce(() => {
        this.pushEventTo('#predictive_search', 'predictive_search', {
          search: this.elements.headerSearchInputDesktop?.value
        });
      }, 250)();
    }
  },
  handleKeyDown(this: This, e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.pushEventTo('#predictive_search', 'redirect_search', {
        search: this.elements.headerSearchInputDesktop?.value
      });
      return;
    }

    this.handleEscape(e);
  },
  // Add event listeners to elements
  addEventListeners(this: This) {
    const elements = document.querySelectorAll('[vulcan-toggle=predictive]');

    // add the event listener toggleOverlay to all elements
    // that have vulcan-toggle=predictive attribute.
    elements.forEach((element) => {
      element.addEventListener('click', this.toggleOverlay.bind(this));
    });

    if (this.closeOnMouseOut) {
      this.elements.predictiveContainer?.addEventListener('mouseleave', () => {
        setTimeout(this.closeOverlay.bind(this), 1000);
      });
    }

    this.el.addEventListener('keydown', this.handleEscape.bind(this));
    this.el.addEventListener('focusout', this.handleFocusOut.bind(this));
    this.elements.closeButton?.addEventListener('keydown', this.handleEnter.bind(this));
    this.elements.closeButton?.addEventListener('click', this.closeOverlay.bind(this));
    this.elements.clearButton?.addEventListener('click', this.handleClear.bind(this));
    this.elements.headerSearchInputDesktop?.addEventListener('focus', this.openOverlay.bind(this));
    this.elements.headerSearchInputDesktop?.addEventListener('input', this.handleInput.bind(this));
    this.elements.headerSearchInputDesktop?.addEventListener(
      'keydown',
      this.handleKeyDown.bind(this)
    );
  },
  // Shows the search overlay modal, adds an overflow css to the HTML body.
  openOverlay(this: This) {
    this.el.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');

    this.elements.input?.focus();
    window.dispatchEvent(new CustomEvent('searchOverlayDisplayedEvent'));

    // For tenants that have a search input in the header (like MAC),
    // we need to make sure that when the search is opened the input is still visible.
    // In order to acomplesh that we change the top property according with the headerVisibleHeight.
    if (this.elements.headerVisible) {
      const headerVisibleHeight = this.elements.headerVisible.getBoundingClientRect().bottom;
      this.el.style.top = isMobile() ? '0px' : `${headerVisibleHeight}px`;
    }

    // Adds click outside event listener callback.
    if (this.elements.predictiveContainer) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  },
  // Hides the search overlay modal, removes the overflow css to the HTML body.
  closeOverlay(this: This) {
    this.el.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');

    this.pushEventTo('#predictive_search', 'clear', {});

    // Removes click outside event listener callback.
    if (this.elements.predictiveContainer) {
      document.removeEventListener('click', this.handleClickOutside.bind(this));
    }

    if (this.elements.headerSearchInputDesktop) {
      this.elements.headerSearchInputDesktop.value = '';
    }
  },
  toggleOverlay(this: This, e: Event) {
    // When a user toggles the overlay, we don't want to dispatch more events.
    e.stopPropagation();
    e.preventDefault();
    this.isOverlayClosed() ? this.openOverlay() : this.closeOverlay();
  },
  setIconFocus(this: This) {
    setTimeout(() => {
      this.elements.searchIconDesktop?.focus();
    }, 0);
  },
  isOverlayClosed(this: This) {
    return this.el.classList.contains('hidden');
  }
};
